import * as z from 'zod';

import { Address, PaymentAddress } from '../Address';
import { CompanyId } from '../BrandedIds';
import { GeneralLedgerCodesReport } from '../companyReports/GeneralLedgerCodesReport';
import { EmailGroupKey } from '../emails/constants/emailGroups';
import { EmailSettings } from '../EmailSettings';
import { MiSettings } from '../investorReporting/MI/MiSettings';
import { CompanyLogos } from '../Logos';
import { PortalSettings } from '../PortalSettings';
import { SupportHours } from '../SupportHours';
import { DEFAULT_OPERATING_TIMEZONE, Timezone } from '../Time';
import { CompanyBillingSettings, DEFAULT_BILLING_SETTINGS } from './BillingSettings';
import { CompanyReportSettings } from './CompanyReportSettings';
import { CompanyThirdPartyIds } from './CompanyThirdPartyIds';
import { DocumentSettings } from './DocumentSettings';
import { CompanyFeeSettings } from './FeeSettings';
import { CompanyLoanControlSettings } from './LoanControlSettings';
import { CompanyNachaSettings } from './NachaSettings';
import { CompanyNonPerformanceAutomationSettings, CompanyNonPerformanceSettings } from './NonPerformanceSettings';
import { CompanyTaskSettings } from './TaskSettings';

export interface Company {
  id: CompanyId;
  name: string;
  modalDisplayName: string;
  supportEmail: string;
  address?: Address;
  paymentAddress?: PaymentAddress;
  supportPhone: string;
  supportHours?: SupportHours;
  website?: string;
  emailSettings: EmailSettings;
  paymentsPaused: boolean;
  adminSettings: CompanyAdminSettings;
  logos: CompanyLogos;
  portalSettings: PortalSettings;
  documentSettings: DocumentSettings;
  nonPerformanceSettings: CompanyNonPerformanceSettings;
  nonPerformanceAutomationSettings: CompanyNonPerformanceAutomationSettings;
  taskSettings: CompanyTaskSettings;
  chatSettings?: CompanyChatSettings;
  feeSettings: CompanyFeeSettings;
  reportSettings?: CompanyReportSettings;
  generalLedgerCodesSettings?: GeneralLedgerCodesReport.Settings;
  ein?: string;
  loanControlSettings: CompanyLoanControlSettings;
  nachaSettings: CompanyNachaSettings;
  thirdPartyIds: CompanyThirdPartyIds;
  miSettings?: MiSettings;
  emailDomain?: string;
  emailFrom?: string;
  disabledEmailGroupKeys: EmailGroupKey[];
  ssoDomains: string[];
  authZeroConnectionNames: string[];
  portalDomain?: string;
}

export interface DwollaCompany {
  id: string;
  companyId: CompanyId;
  customerUrl: string;
  primaryBankAccount?: string;
  escrowBankAccount?: string;
  suspenseBankAccount?: string;
  sweepEnabled: boolean;
  plaidToken?: string;
  plaidAccountId?: string;
  plaidMicrodepositPostedEmailSent: boolean;
}

export const CompanyAdminSettings = z.object({
  // demo
  whedaEnabled: z.boolean().optional(),

  // lender one-offs
  loanExtensionAgreementDocumentEnabled: z.boolean().optional(), // elite only
  loanExtensionFeesDocumentEnabled: z.boolean().optional(), // elite only

  // wip
  wipInvestorReporting: z.boolean().optional(),
  wipFileImport: z.boolean().optional(),
  wipBankruptcyCh13WorkoutPlan: z.boolean().optional(),
  wipDefaultPage: z.boolean().optional(),

  sendTransferEmailImmediately: z.boolean(),
  autopayEnabled: z.boolean(),
  mortgageeLettersEnabled: z.boolean(),
  investorRemittanceEnabled: z.boolean(),
  disableBorrowerNotifications: z.boolean().optional(), // FOLLOWUP: rename disableAllLoanNotifications
  multiplePropertiesEnabled: z.boolean().optional(),
  generate1098Enabled: z.boolean().optional(),
  generate1098InterestThreshold: z.number().optional(),
  automateNonPerformingLoans: z.boolean(),
  enableUsioPayments: z.boolean().optional(),
  enableSameDayACH: z.boolean().optional(),
  enableEncryptedAccountDetailsStorage: z.boolean().optional(),
  enableBorrowerInsuranceExpirationEmailReminders: z.boolean().optional(),
  insuranceTaxTrackingEnabled: z.boolean(),
  newInsuranceFieldsEnabled: z.boolean().optional(),
  skipEscrowTrackingWithin60Days: z.boolean(),
  applicationsEnabled: z.boolean().optional(),
  applicationsEnabledSettings: z
    .object({
      ladderLife: z.boolean(),
    })
    .optional(),
  econsentEnabled: z.boolean().optional(),
  printAndMailEnabled: z.boolean().optional(),
  loanWorkflowsEnabled: z.boolean().optional(),
  chapter13WorkflowEnabled: z.boolean().optional(),
  billingSettings: CompanyBillingSettings,
  investorView: z.boolean().optional(),
  mfaAppUsersEnabled: z.boolean().optional(),
  creditPendingPaymentsOnPayoff: z.boolean().optional(),
  exactPaymentsOnly: z.boolean().optional(),

  noteflowEnabled: z.boolean().optional(),
  repayEnabled: z.boolean().optional(),
  glCodesReportEnabled: z.boolean().optional(),
  workflowsV2Enabled: z.boolean().optional(),
  allowlistEmailDomains: z.array(z.string()).optional(),
  teamsEnabled: z.boolean().optional(),
  manualStatementGeneration: z.boolean().optional(),
  batchBoardingStatementGeneration: z.boolean().optional(),
  poolManagement: z.boolean().optional(),
  custodialAccountsEnabled: z.boolean().optional(),
  investorDocumentsEnabled: z.boolean().optional(),
  investorAuditLogEnabled: z.boolean().optional(),
  includePayoffFundingDateEnabled: z.boolean().optional(),
  eliteFees: z.boolean().optional(),
  wipCreditReporting: z.boolean().optional(),
  wipHistoricalPayments: z.boolean().optional(),
  aiLoanNotesSummaryEnabled: z.boolean().optional(),
  aiEmailsEnabled: z.boolean().optional(),
  dwollaSingleTransactionEnabled: z.boolean().optional(),
  repayProperAccountRelationship: z.boolean().optional(),
  repayDirectBankIntegration: z.boolean().optional(),
  customTemplates: z.boolean().optional(),
  manualNachaFileGeneration: z.boolean().optional(),
  dataExportEnabled: z.boolean().optional(),
  dataLakeSyncEnabled: z.boolean().optional(),
  // 0 (or absent) inherits the environment's default cadence.
  dataLakeSyncIntervalMinutes: z.number().int().min(0).optional(),
  shouldValidateBreakdownsNotMutated: z.boolean().optional(),
  interestOnEscrow: z.boolean().optional(),
  escrowAdvanceReporting: z.boolean().optional(),
  payeeManagement: z.boolean().optional(),
  // Per-company opt-in for the typed-postings ledger; prod defaults
  // to `undefined` (off) and admins flip it per company at enrollment.
  ledgerEnabled: z.boolean().optional(),

  // Timezone where the company physically operates; used to resolve timestamps to calendar
  // dates for the company's reports and exports. Access via `getCompanyOperatingTimezone`.
  operatingTimezone: Timezone.optional(),
});

export type CompanyAdminSettings = z.infer<typeof CompanyAdminSettings>;
export type CompanyAdminSetting = keyof NonNullable<CompanyAdminSettings>;

export const DEFAULT_COMPANY_ADMIN_SETTINGS: Required<CompanyAdminSettings> = {
  // demo only
  whedaEnabled: false,

  // wip
  wipInvestorReporting: false,
  wipFileImport: false,
  wipBankruptcyCh13WorkoutPlan: false,
  wipDefaultPage: false,

  // documents
  loanExtensionAgreementDocumentEnabled: false,
  loanExtensionFeesDocumentEnabled: false,

  // features
  sendTransferEmailImmediately: false,
  autopayEnabled: true,
  mortgageeLettersEnabled: false,
  investorRemittanceEnabled: false,
  disableBorrowerNotifications: false,
  multiplePropertiesEnabled: false,
  generate1098Enabled: true,
  generate1098InterestThreshold: 600,
  automateNonPerformingLoans: false,
  enableUsioPayments: false,
  enableSameDayACH: false,
  enableEncryptedAccountDetailsStorage: true,
  enableBorrowerInsuranceExpirationEmailReminders: false,
  insuranceTaxTrackingEnabled: false,
  newInsuranceFieldsEnabled: false,
  skipEscrowTrackingWithin60Days: true,
  applicationsEnabled: false,
  applicationsEnabledSettings: {
    ladderLife: false,
  },
  econsentEnabled: false,
  printAndMailEnabled: false,
  loanWorkflowsEnabled: false,
  chapter13WorkflowEnabled: false,
  billingSettings: DEFAULT_BILLING_SETTINGS,
  investorView: false,
  mfaAppUsersEnabled: false,
  creditPendingPaymentsOnPayoff: false,
  exactPaymentsOnly: false,
  noteflowEnabled: false,
  repayEnabled: false,
  glCodesReportEnabled: false,
  workflowsV2Enabled: false,
  allowlistEmailDomains: [],
  teamsEnabled: false,
  manualStatementGeneration: false,
  batchBoardingStatementGeneration: true,
  poolManagement: false,
  custodialAccountsEnabled: false,
  investorDocumentsEnabled: false,
  investorAuditLogEnabled: false,
  includePayoffFundingDateEnabled: false,
  eliteFees: false,
  wipCreditReporting: false,
  wipHistoricalPayments: true,
  aiLoanNotesSummaryEnabled: false,
  aiEmailsEnabled: false,
  dwollaSingleTransactionEnabled: true,
  repayProperAccountRelationship: false,
  repayDirectBankIntegration: false,
  customTemplates: false,
  manualNachaFileGeneration: false,
  dataExportEnabled: false,
  dataLakeSyncEnabled: false,
  dataLakeSyncIntervalMinutes: 0,

  shouldValidateBreakdownsNotMutated: false,
  interestOnEscrow: false,
  escrowAdvanceReporting: false,
  payeeManagement: false,
  ledgerEnabled: false,
  operatingTimezone: DEFAULT_OPERATING_TIMEZONE,
};

export const DEV_DEFAULT_COMPANY_ADMIN_SETTINGS: CompanyAdminSettings = {
  // demo only
  whedaEnabled: false,

  // wip
  wipFileImport: true,
  wipCreditReporting: false,
  wipHistoricalPayments: true,
  wipInvestorReporting: true,
  wipBankruptcyCh13WorkoutPlan: true,
  wipDefaultPage: true,

  // features
  sendTransferEmailImmediately: false,
  autopayEnabled: true,
  mortgageeLettersEnabled: true,
  investorRemittanceEnabled: true,
  disableBorrowerNotifications: false,
  multiplePropertiesEnabled: true,
  generate1098Enabled: true,
  generate1098InterestThreshold: 600,
  automateNonPerformingLoans: false,
  enableUsioPayments: true,
  enableSameDayACH: true,
  enableEncryptedAccountDetailsStorage: false,
  enableBorrowerInsuranceExpirationEmailReminders: true,
  insuranceTaxTrackingEnabled: true,
  newInsuranceFieldsEnabled: true,
  skipEscrowTrackingWithin60Days: false,
  applicationsEnabled: true,
  applicationsEnabledSettings: {
    ladderLife: true,
  },
  econsentEnabled: true,
  printAndMailEnabled: true,
  loanWorkflowsEnabled: false,
  chapter13WorkflowEnabled: true,
  billingSettings: DEFAULT_BILLING_SETTINGS,
  investorView: false,
  mfaAppUsersEnabled: false,
  creditPendingPaymentsOnPayoff: true,
  exactPaymentsOnly: false,
  noteflowEnabled: true,
  repayEnabled: false,
  glCodesReportEnabled: true,
  workflowsV2Enabled: true,
  teamsEnabled: true,
  manualStatementGeneration: true,
  batchBoardingStatementGeneration: true,
  poolManagement: true,
  custodialAccountsEnabled: true,

  // documents
  loanExtensionAgreementDocumentEnabled: false,
  loanExtensionFeesDocumentEnabled: false,

  investorDocumentsEnabled: true,
  investorAuditLogEnabled: true,
  includePayoffFundingDateEnabled: true,
  eliteFees: false,

  aiLoanNotesSummaryEnabled: true,
  aiEmailsEnabled: true,
  dwollaSingleTransactionEnabled: true,
  repayProperAccountRelationship: true,
  repayDirectBankIntegration: false,
  customTemplates: false,
  interestOnEscrow: true,
  escrowAdvanceReporting: true,
  manualNachaFileGeneration: false,
  dataExportEnabled: false,
  // Deliberately off even in dev: enabling auto-provisions real AWS resources.
  dataLakeSyncEnabled: false,
  dataLakeSyncIntervalMinutes: 0,
  payeeManagement: true,
  ledgerEnabled: true,
  operatingTimezone: DEFAULT_OPERATING_TIMEZONE,
};

export const getCompanyOperatingTimezone = (company: Pick<Company, 'adminSettings'>): Timezone =>
  company.adminSettings.operatingTimezone ?? DEFAULT_OPERATING_TIMEZONE;

export const DEFAULT_COMPANY_LOGOS: CompanyLogos = {
  portalLogoUrl: undefined,
  emailLogoUrl: undefined,
};

export const CompanyChatSettings = z.object({
  chatGptContext: z.string().optional(),
});
export type CompanyChatSettings = z.infer<typeof CompanyChatSettings>;
